import React, { useState, useEffect, useCallback } from 'react';
import { ExploreConfig, PromptBuilder, PlatformAdapter, Analytics, Audience, AIPlatform } from '@explore-with-ai/core';
import './AIHandoffPage.css';

interface AIHandoffPageProps {
  config: ExploreConfig;
  mode: string;
  audience?: Audience;
  onBack: () => void;
}

const AIHandoffPage: React.FC<AIHandoffPageProps> = ({ config, mode, audience, onBack }) => {
  const [prompt, setPrompt] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [copied, setCopied] = useState(false);
  const [analytics] = useState(() => new Analytics());

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      analytics.trackPromptCopied(config.id, !!config.context);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  }, [analytics, config.id, config.context]);

  useEffect(() => {
    console.log('AIHandoffPage - Generating prompt with:', { mode, audience, hasContext: !!config.context });
    const promptBuilder = new PromptBuilder(config);
    const generatedPrompt = promptBuilder.buildPrompt(mode, audience, config.context);
    console.log('Generated prompt:', generatedPrompt);
    setPrompt(generatedPrompt);
    setEditedPrompt(generatedPrompt);

    // Auto-copy prompt on load
    copyToClipboard(generatedPrompt);
  }, [config, mode, audience, copyToClipboard]);

  const handlePromptEdit = (value: string) => {
    setEditedPrompt(value);
    if (value !== prompt) {
      analytics.track('explore_ai_prompt_edited', {
        datasetId: config.id,
        datasetTitle: config.title,
        promptEdited: true
      }, config.id);
    }
  };

  const handlePlatformLaunch = (platform: AIPlatform) => {
    const finalPrompt = isEditing ? editedPrompt : prompt;
    
    // Track platform selection
    analytics.track('explore_ai_platform_selected', {
      datasetId: config.id,
      datasetTitle: config.title,
      platform,
      mode,
      audience,
      contextPresent: !!config.context
    }, config.id);
    
    PlatformAdapter.launchPlatform(platform, finalPrompt);
    
    // Track successful launch
    analytics.track('explore_ai_launch', {
      datasetId: config.id,
      datasetTitle: config.title,
      platform,
      mode,
      audience,
      contextPresent: !!config.context
    }, config.id);
  };

  const currentPrompt = isEditing ? editedPrompt : prompt;

  return (
    <div className="ai-handoff">
      <div className="ai-handoff__header">
        <button onClick={onBack} className="ai-handoff__back-button">
          ← Back to Configuration
        </button>
        <h1 className="ai-handoff__title">Ready to Explore with AI</h1>
      </div>

      <div className="ai-handoff__content">
        <div className="ai-handoff__dataset-info">
          <h2>{config.title}</h2>
          <p className="ai-handoff__description">{config.description}</p>
          
          <div className="ai-handoff__meta">
            <div className="ai-handoff__meta-item">
              <strong>Analysis Mode:</strong> {mode}
            </div>
            {audience && (
              <div className="ai-handoff__meta-item">
                <strong>Target Audience:</strong> {audience.charAt(0).toUpperCase() + audience.slice(1)}
              </div>
            )}
            {config.context && (
              <div className="ai-handoff__meta-item">
                <strong>Context:</strong> Real-time page context included
              </div>
            )}
          </div>
        </div>

        <div className="ai-handoff__prompt-section">
          <div className="ai-handoff__prompt-header">
            <h3>Generated Prompt</h3>
            <div className="ai-handoff__prompt-actions">
              <button
                onClick={() => setIsEditing(!isEditing)}
                className={`ai-handoff__edit-button ${isEditing ? 'ai-handoff__edit-button--active' : ''}`}
              >
                {isEditing ? 'Preview' : 'Edit'}
              </button>
              <button
                onClick={() => copyToClipboard(currentPrompt)}
                className="ai-handoff__copy-button"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {isEditing ? (
            <textarea
              value={editedPrompt}
              onChange={(e) => handlePromptEdit(e.target.value)}
              className="ai-handoff__prompt-editor"
              rows={12}
              placeholder="Edit your prompt here..."
            />
          ) : (
            <div className="ai-handoff__prompt-preview">
              <pre>{currentPrompt}</pre>
            </div>
          )}
        </div>

        <div className="ai-handoff__platforms">
          <h3>Choose Your AI Platform</h3>
          <p className="ai-handoff__platforms-description">
            The prompt has been copied to your clipboard. Click a platform to open it with the prompt.
          </p>
          
          <div className="ai-handoff__platform-grid">
            <button
              onClick={() => handlePlatformLaunch('chatgpt')}
              className="ai-handoff__platform-button ai-handoff__platform-button--chatgpt"
            >
              <div className="ai-handoff__platform-icon">🤖</div>
              <div className="ai-handoff__platform-info">
                <h4>ChatGPT</h4>
                <p>Opens with prompt pre-filled</p>
              </div>
            </button>

            <button
              onClick={() => handlePlatformLaunch('claude')}
              className="ai-handoff__platform-button ai-handoff__platform-button--claude"
            >
              <div className="ai-handoff__platform-icon">🧠</div>
              <div className="ai-handoff__platform-info">
                <h4>Claude</h4>
                <p>Opens with prompt pre-filled</p>
              </div>
            </button>

            <button
              onClick={() => handlePlatformLaunch('gemini')}
              className="ai-handoff__platform-button ai-handoff__platform-button--gemini"
            >
              <div className="ai-handoff__platform-icon">💎</div>
              <div className="ai-handoff__platform-info">
                <h4>Gemini</h4>
                <p>Opens platform - paste prompt</p>
              </div>
            </button>

            <button
              onClick={() => handlePlatformLaunch('perplexity')}
              className="ai-handoff__platform-button ai-handoff__platform-button--perplexity"
            >
              <div className="ai-handoff__platform-icon">🔍</div>
              <div className="ai-handoff__platform-info">
                <h4>Perplexity</h4>
                <p>Opens with prompt pre-filled</p>
              </div>
            </button>

            <button
              onClick={() => handlePlatformLaunch('grok')}
              className="ai-handoff__platform-button ai-handoff__platform-button--grok"
            >
              <div className="ai-handoff__platform-icon">⚡</div>
              <div className="ai-handoff__platform-info">
                <h4>Grok</h4>
                <p>Opens with prompt pre-filled</p>
              </div>
            </button>
          </div>
        </div>

        <div className="ai-handoff__fallback">
          <h4>Having trouble?</h4>
          <p>
            If the platform doesn't open automatically, the prompt has been copied to your clipboard. 
            Simply open your preferred AI platform and paste it in.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIHandoffPage;
